import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function AnalyticsDashboard({ admin }: { admin: any }) {
  const [analytics, setAnalytics] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    totalJobs: 0,
    completedJobs: 0,
    activeProviders: 0,
    averageRating: 0,
    topProviders: [] as any[],
    revenueByMonth: [] as any[],
    jobsByService: [] as any[]
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - parseInt(timeRange));

    // Load all data
    const [invoicesData, jobsData, providersData] = await Promise.all([
      supabase.from('invoices').select('*'),
      supabase.from('jobs').select('*'),
      supabase.from('provider_profiles').select('*')
    ]);

    // Calculate total revenue
    const totalRevenue = invoicesData.data
      ?.filter(i => i.status === 'paid')
      .reduce((sum, i) => sum + parseFloat(i.total_amount || 0), 0) || 0;

    // Calculate monthly revenue
    const monthlyRevenue = invoicesData.data
      ?.filter(i => i.status === 'paid' && new Date(i.created_at) >= daysAgo)
      .reduce((sum, i) => sum + parseFloat(i.total_amount || 0), 0) || 0;

    // Jobs stats
    const totalJobs = jobsData.data?.length || 0;
    const completedJobs = jobsData.data?.filter(j => j.status === 'completed').length || 0;

    // Provider stats
    const activeProviders = providersData.data?.filter(p => p.status === 'approved').length || 0;
    const averageRating = providersData.data
      ?.reduce((sum, p) => sum + (parseFloat(p.rating) || 0), 0) / (providersData.data?.length || 1) || 0;

    // Top providers by earnings
    const topProviders = providersData.data
      ?.sort((a, b) => (b.total_earnings || 0) - (a.total_earnings || 0))
      .slice(0, 5) || [];

    // Revenue by month (last 6 months)
    const revenueByMonth = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const monthRevenue = invoicesData.data
        ?.filter(inv => {
          const invDate = new Date(inv.created_at);
          return inv.status === 'paid' && invDate >= monthStart && invDate <= monthEnd;
        })
        .reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0) || 0;

      revenueByMonth.push({
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        revenue: monthRevenue
      });
    }

    // Jobs by service type
    const serviceTypes: { [key: string]: number } = {};
    jobsData.data?.forEach(job => {
      const service = job.service_type || 'Other';
      serviceTypes[service] = (serviceTypes[service] || 0) + 1;
    });

    const jobsByService = Object.entries(serviceTypes)
      .map(([service, count]) => ({ service, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    setAnalytics({
      totalRevenue,
      monthlyRevenue,
      totalJobs,
      completedJobs,
      activeProviders,
      averageRating,
      topProviders,
      revenueByMonth,
      jobsByService
    });

    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line text-4xl text-purple-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
          <p className="text-gray-600 mt-1">Platform performance and insights</p>
        </div>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm cursor-pointer"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
        </select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">£{analytics.totalRevenue.toFixed(2)}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-money-pound-circle-line text-2xl text-green-600 w-6 h-6 flex items-center justify-center"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Period Revenue</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">£{analytics.monthlyRevenue.toFixed(2)}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-line-chart-line text-2xl text-blue-600 w-6 h-6 flex items-center justify-center"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed Jobs</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{analytics.completedJobs}</p>
              <p className="text-sm text-gray-500 mt-1">of {analytics.totalJobs} total</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-2xl text-purple-600 w-6 h-6 flex items-center justify-center"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Providers</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{analytics.activeProviders}</p>
              <p className="text-sm text-gray-500 mt-1">Avg: {analytics.averageRating.toFixed(1)} ⭐</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <i className="ri-team-line text-2xl text-orange-600 w-6 h-6 flex items-center justify-center"></i>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Revenue Chart */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend (Last 6 Months)</h3>
          <div className="space-y-3">
            {analytics.revenueByMonth.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">{item.month}</span>
                  <span className="text-sm font-medium text-gray-900">£{item.revenue.toFixed(2)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full"
                    style={{
                      width: `${Math.min((item.revenue / Math.max(...analytics.revenueByMonth.map(m => m.revenue))) * 100, 100)}%`
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Jobs by Service */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Services</h3>
          <div className="space-y-3">
            {analytics.jobsByService.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">{item.service}</span>
                  <span className="text-sm font-medium text-gray-900">{item.count} jobs</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{
                      width: `${(item.count / analytics.totalJobs) * 100}%`
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Providers */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Providers</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Earnings</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jobs Completed</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics.topProviders.map((provider, index) => (
                <tr key={provider.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-2xl font-bold text-gray-400">#{index + 1}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-semibold">
                        {provider.full_name[0].toUpperCase()}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{provider.full_name}</div>
                        <div className="text-sm text-gray-500">{provider.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-lg font-bold text-green-600">£{(provider.total_earnings || 0).toFixed(2)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {provider.completed_jobs} / {provider.total_jobs}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-yellow-600">{(provider.rating || 0).toFixed(1)} ⭐</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
